import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useAuth } from '../../../context/Auth';
import { RootNavigation } from '../../../Routes';
import { useQuery } from '@tanstack/react-query';
import { appAgentService, FuelType } from '../../../services/central-api/app-agent';
import moment from 'moment';
import { useForm, useWatch } from 'react-hook-form';
import stringToFileImage from '../../../services/stringToFileImage';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { UserNotInUnit } from '../../../services/central-api/base-service';

export interface RasItem {
    id?: number;
    indicatorId: number;
    actionsQty?: number;
    publicQty?: number;
    documentNumber?: number;
    startTime: string;
    endTime: string;
    useParentAddress: boolean;
    cep?: string;
    address?: string;
    number?: string;
    latitude?: string;
    longitude?: string;
}

export interface AttachmentItem {
    id?: number;
    imageUrl: string;
    description?: string;
}

interface PolicePatrolScheduleForm {
    cep: string;
    address: string;
    number: string | undefined;
    date?: Date;
    startTime: string;
    endTime?: string;
    activityId: number;
    observations?: string;
    odometer: number | undefined;
    latitude: string;
    longitude: string;
    cityPropertyId: number;
    attachments: AttachmentItem[];
    occurrenceIds: number[];
    multipleRas: RasItem[];
    fuelType?: FuelType;
    fuelQuantity?: string;
    fuelTotalValue?: string;
    pumpNumber?: string;
}

export function formatDuration(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) {
        return `${minutes}min`;
    }
    if (minutes === 0) {
        return `${hours}h`;
    }
    return `${hours}h${minutes}min`;
}

export function anchorEndTime(endTime: string, start: moment.Moment): moment.Moment {
    const [hours, minutes] = endTime.split(':').map(Number);
    let end = start.clone().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

    if (end.isBefore(start)) {
        end = end.add(1, 'day');
    }

    return end;
}

export function computeEffectiveRange(startTime?: string, endTime?: string, date?: Date, definitiveStart?: string | Date) {
    if (!startTime) {
        return null;
    }

    let start: moment.Moment | null = null;

    if (date) {
        const [hours, minutes] = startTime.split(':').map(Number);
        start = moment(date).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
    } else if (definitiveStart) {
        const shiftStart = moment(definitiveStart);
        const [hours, minutes] = startTime.split(':').map(Number);

        start = shiftStart.clone().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

        if (start.isBefore(shiftStart)) {
            start = start.add(1, 'day');
        }
    }

    if (!start) {
        return null;
    }

    let end: moment.Moment | null = null;
    if (endTime) {
        end = anchorEndTime(endTime, start);
    }

    return { start, end };
}

export function usePolicePatrolScheduleDetails({
    activityId,
    suggestedOdometer,
    isFinalizing,
    navigation,
}: {
    activityId?: number;
    suggestedOdometer?: number;
    isFinalizing: boolean;
    navigation: RootNavigation;
}) {
    const { user: authenticatedUser } = useAuth();
    const isFocused = useIsFocused();

    const [pin, setPin] = useState<LatLng | undefined>(undefined);
    const [sendingData, setSendingData] = useState<boolean>(false);
    const [datePickerZIndex, setDatePickerZIndex] = useState<number>(3);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [isAttachmentRequired, setIsAttachmentRequired] = useState<boolean>(false);
    const [hasRefuel, setHasRefuel] = useState<boolean>(false);

    const settingCepInternallyRef = useRef('');

    const policePatrolScheduleQuery = useQuery({
        queryKey: ['getPolicePatrolScheduleById', activityId],
        queryFn: () => appAgentService.getPolicePatrolScheduleById(activityId!),
        enabled: !!authenticatedUser && isFocused && !!activityId,
    });
    const { data } = policePatrolScheduleQuery;

    const activeUnitQuery = useQuery({
        queryKey: ['getUserActiveUnit'],
        queryFn: () => appAgentService.getAgentActiveUnit(),
        enabled: !!authenticatedUser && isFocused,
    });
    const { data: activeUnit } = activeUnitQuery;

    const isLoading = !!authenticatedUser && (policePatrolScheduleQuery.isFetching || activeUnitQuery.isFetching);
    const queriesReady = !!authenticatedUser && (!activityId || policePatrolScheduleQuery.isFetched) && activeUnitQuery.isFetched;

    const defaultValues: PolicePatrolScheduleForm = useMemo(() => ({
        cep: '',
        address: '',
        number: undefined,
        date: undefined,
        startTime: moment().format('HH:mm'),
        endTime: undefined,
        odometer: suggestedOdometer ?? undefined,
        observations: undefined,
        activityId: 0,
        latitude: '',
        longitude: '',
        cityPropertyId: 0,
        attachments: [],
        multipleRas: [],
        occurrenceIds: [],
        fuelType: undefined,
        fuelQuantity: undefined,
        fuelTotalValue: undefined,
        pumpNumber: undefined,
    }), [suggestedOdometer]);

    const form = useForm<PolicePatrolScheduleForm>({
        defaultValues,
        mode: 'onChange',
    });

    const values = useWatch({ control: form.control });

    const maskDecimal = (digits: string) =>
        (Number(digits) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const toDigits = (value: string) => String(Math.round(Number(value) * 100));
    const toDecimalString = (digits?: string) => (digits ? (Number(digits) / 100).toFixed(2) : undefined);

    useEffect(() => {
        form.register('address', { required: true });
        form.register('latitude', { required: true });
        form.register('longitude', { required: true });
    }, [form]);

    useEffect(() => {
        const requiresAttachment = isAttachmentRequired && (isFinalizing || !!values.endTime);

        form.register('attachments', {
            validate: (value) => {
                if (requiresAttachment && (!value || value.length === 0)) {
                    return translate('required_field');
                }
                return true;
            },
        });

        form.trigger('attachments');
    }, [isAttachmentRequired, isFinalizing, values.endTime, values.attachments, form]);

    useEffect(() => {
        const requiresOdometer = isFinalizing || !!values.endTime;

        form.register('odometer', {
            validate: (value) => {
                if (requiresOdometer && (value === undefined || value === null || value === 0)) {
                    return translate('required_field');
                }
                return true;
            },
        });

        form.trigger('odometer');
    }, [isFinalizing, values.endTime, form]);

    useEffect(() => {
        if (!isFocused || !queriesReady) {
            return;
        }

        if (!data) {
            form.reset({
                ...defaultValues,
                date: moment().toDate(),
            });
            setPin(undefined);
            return;
        }

        settingCepInternallyRef.current = data.cep;
        setPin(data.latitude && data.longitude ? { lat: Number(data.latitude), lng: Number(data.longitude) } : undefined);

        form.reset({
            cep: data.cep,
            address: data.address,
            number: data.number,
            date: moment(data.startDate).toDate(),
            startTime: moment(data.startDate).format('HH:mm'),
            endTime: data.endDate
                ? moment(data.endDate).format('HH:mm')
                : (isFinalizing ? moment().format('HH:mm') : undefined),
            observations: data.observations ?? undefined,
            odometer: data.odometer ? data.odometer : (suggestedOdometer ?? undefined),
            activityId: data.activityId,
            latitude: data.latitude,
            longitude: data.longitude,
            attachments: data.attachments?.map((a) => ({
                id: a.id,
                imageUrl: a.imageUrl,
                description: a.description ?? undefined,
            })) ?? [],
            occurrenceIds: data.occurrences?.map((o) => o.id) ?? [],
            multipleRas: data.activityAndServices?.length
                ? data.activityAndServices.map((ac) => {
                    const useParentAddress = ac.latitude === data.latitude && ac.longitude === data.longitude;
                    return {
                        id: ac.id,
                        actionsQty: ac.actionsQty,
                        publicQty: ac.publicQty,
                        documentNumber: ac.documentNumber,
                        indicatorId: ac.indicatorId,
                        startTime: moment(ac.startDate).format('HH:mm'),
                        endTime: moment(ac.endDate).format('HH:mm'),
                        useParentAddress,
                        cep: useParentAddress ? undefined : ac.cep,
                        address: useParentAddress ? undefined : ac.address,
                        number: useParentAddress ? undefined : ac.number,
                        latitude: useParentAddress ? undefined : ac.latitude,
                        longitude: useParentAddress ? undefined : ac.longitude,
                    };
                })
                : [],
            fuelType: data.fuelType ?? undefined,
            fuelQuantity: data.fuelQuantity != null ? toDigits(data.fuelQuantity) : undefined,
            fuelTotalValue: data.fuelTotalValue != null ? toDigits(data.fuelTotalValue) : undefined,
            pumpNumber: data.pumpNumber ?? undefined,
        });
    }, [isFocused, queriesReady, data]);

    useEffect(() => {
        if (hasRefuel) {
            return;
        }
        form.setValue('fuelType', undefined);
        form.setValue('fuelQuantity', undefined);
        form.setValue('fuelTotalValue', undefined);
        form.setValue('pumpNumber', undefined);
    }, [hasRefuel, form]);

    useEffect(() => {
        if (hasRefuel) {
            form.trigger(['fuelType', 'fuelQuantity', 'fuelTotalValue', 'pumpNumber']);
        }
    }, [hasRefuel, values.endTime, form]);

    const activityRange = useMemo(
        () => computeEffectiveRange(values.startTime, values.endTime, values.date, activeUnit?.definitiveStart ?? undefined),
        [values.startTime, values.endTime, values.date, activeUnit?.definitiveStart]
    );

    const activityRangeLabel = useMemo(() => {
        if (!activityRange?.start || !activityRange?.end) {
            return null;
        }

        const { start, end } = activityRange;
        const crossesMidnight = !start.isSame(end, 'day');
        const isSameValue = start.isSame(end);
        const durationMinutes = end.diff(start, 'minutes');

        return {
            rangeText: crossesMidnight
                ? `${start.format('DD/MM HH:mm')} → ${end.format('DD/MM HH:mm')}`
                : `${start.format('DD/MM')} · ${start.format('HH:mm')} → ${end.format('HH:mm')}`,
            durationText: formatDuration(durationMinutes),
            crossesMidnight,
            isSameValue,
        };
    }, [activityRange]);

    const clearValues = () => {
        form.reset(defaultValues);
        setPin(undefined);
        setHasRefuel(false);
    };

    const navigateBackOrHome = () => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.navigate('AppAgent', { screen: 'PolicePatrolSchedule' });
        }
    };

    const formSubmit = async () => {
        const formValues = form.getValues();
        const dates = computeEffectiveRange(formValues.startTime, formValues.endTime, formValues.date, activeUnit?.definitiveStart ?? undefined);

        if (dates?.end) {
            const rasEnds = formValues.multipleRas
                .map((item) =>
                    computeEffectiveRange(
                        item.startTime,
                        item.endTime,
                        undefined,
                        dates.start.toDate()
                    )?.end
                )
                .filter((end): end is moment.Moment => !!end);

            if (rasEnds.length > 0) {
                const latestEnd = rasEnds.reduce((latest, current) =>
                    current.isAfter(latest) ? current : latest
                );

                if (dates.end.isBefore(latestEnd)) {
                    Toast.show({
                        type: 'sentinelxError',
                        text1: `${translate('patrolEndTimeMustCoverActivities')} (${latestEnd.format('DD/MM HH:mm')})`,
                    });
                    return;
                }
            }
        }

        const syncActivityAndServices = async (policePatrolScheduleId: number) => {
            if (!dates) {
                return;
            }

            const existingServices = data?.activityAndServices ?? [];
            const currentRasIds = formValues.multipleRas.map((item) => item.id).filter(Boolean);

            const toDelete = existingServices.filter(
                (service) => !currentRasIds.includes(service.id)
            );

            if (toDelete.length > 0) {
                await Promise.all(
                    toDelete.map((service) =>
                        appAgentService.deleteActivityAndService(service.id)
                    )
                );
            }

            await Promise.all(
                formValues.multipleRas.map(async (item) => {
                    const rasRange = computeEffectiveRange(
                        item.startTime,
                        item.endTime,
                        undefined,
                        dates.start.toDate()
                    );

                    if (!rasRange?.end) {
                        throw new Error('RAS sem período válido');
                    }

                    const rasPayload = {
                        policePatrolScheduleId,
                        actionsQty: Number(item.actionsQty || 0),
                        publicQty: Number(item.publicQty || 0),
                        documentNumber: item.documentNumber
                            ? Number(item.documentNumber)
                            : undefined,
                        indicatorId: item.indicatorId,
                        cep: item.useParentAddress ? formValues.cep : item.cep!,
                        address: item.useParentAddress ? formValues.address : item.address!,
                        number: item.useParentAddress ? formValues.number : item.number,
                        latitude: Number(
                            item.useParentAddress ? formValues.latitude : item.latitude
                        ),
                        longitude: Number(
                            item.useParentAddress ? formValues.longitude : item.longitude
                        ),
                        startDate: rasRange.start.toDate(),
                        endDate: rasRange.end.toDate(),
                    };

                    if (item.id) {
                        await appAgentService.updateActivityAndService({
                            id: item.id,
                            ...rasPayload,
                        });
                    } else {
                        await appAgentService.createActivityAndService(rasPayload);
                    }
                })
            );
        };

        const previousEnd = data?.endDate ? moment(data.endDate) : null;

        const mustSyncRasBeforeRdp =
            !!activityId &&
            !!dates?.end &&
            (!previousEnd || dates.end.isBefore(previousEnd));

        setSendingData(true);
        try {
            let savedActivityId = activityId;

            if (activityId) {
                if (mustSyncRasBeforeRdp) {
                    await syncActivityAndServices(activityId);
                }

                await appAgentService.updatePolicePatrolSchedule({
                    id: activityId,
                    cep: formValues.cep,
                    address: formValues.address,
                    number: formValues.number,
                    startDate: dates?.start ? moment(dates.start).toDate() : undefined,
                    endDate: dates?.end ? moment(dates.end).toDate() : undefined,
                    odometer: formValues.odometer,
                    observations: formValues.observations,
                    activityId: formValues.activityId,
                    latitude: Number(formValues.latitude),
                    longitude: Number(formValues.longitude),
                    occurrenceIds: formValues.occurrenceIds.length ? formValues.occurrenceIds : [],
                    fuelType: hasRefuel ? formValues.fuelType : undefined,
                    fuelQuantity: hasRefuel ? toDecimalString(formValues.fuelQuantity) : undefined,
                    fuelTotalValue: hasRefuel ? toDecimalString(formValues.fuelTotalValue) : undefined,
                    pumpNumber: hasRefuel ? (formValues.pumpNumber || undefined) : undefined,
                });

                const existingAttachments = data?.attachments ?? [];
                const currentAttachmentIds = formValues.attachments.map((a) => a.id).filter(Boolean);

                const attachmentsToDelete = existingAttachments.filter((a) => !currentAttachmentIds.includes(a.id));
                if (attachmentsToDelete.length > 0) {
                    await Promise.all(
                        attachmentsToDelete.map((a) => appAgentService.policePatrolScheduleDeleteAttachment(a.id))
                    );
                }

                await Promise.all(
                    formValues.attachments.map(async (item) => {
                        if (!item.id) {
                            const fileImage = stringToFileImage(item.imageUrl);
                            await appAgentService.updatePolicePatrolScheduleAttachment(
                                { id: activityId, description: item.description != '' ? item.description : null },
                                fileImage
                            );
                            return;
                        }

                        const existing = existingAttachments.find((a) => a.id === item.id);
                        const descriptionChanged = (existing?.description ?? undefined) !== (item.description ?? undefined);


                        if (descriptionChanged) {
                            await appAgentService.updatePolicePatrolScheduleAttachment(
                                { id: activityId, attachmentId: item.id, description: item.description != '' ? item.description : null },
                                undefined
                            );
                        }
                    })
                );

            } else {
                const created = await appAgentService.createPolicePatrolSchedule({
                    cep: formValues.cep,
                    address: formValues.address,
                    number: formValues.number,
                    startDate: moment(dates?.start ?? moment()).toDate(),
                    activityId: formValues.activityId,
                    latitude: Number(formValues.latitude),
                    longitude: Number(formValues.longitude),
                    occurrenceIds: formValues.occurrenceIds.length ? formValues.occurrenceIds : [],
                });
                savedActivityId = created?.id;
            }

            if (savedActivityId && dates && !mustSyncRasBeforeRdp) {
                await syncActivityAndServices(savedActivityId);
            }

            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            clearValues();
            navigateBackOrHome();
        } catch (err) {
            if (err instanceof UserNotInUnit) {
                return Toast.show({ type: 'sentinelxError', text1: translate('notActiveInUnit') });
            }
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        } finally {
            setSendingData(false);
        }
    };

    return {
        form,
        values,
        data,
        activeUnit,
        isLoading,
        sendingData,
        pin,
        setPin,
        datePickerZIndex,
        setDatePickerZIndex,
        isDeleteModalVisible,
        setIsDeleteModalVisible,
        isAttachmentRequired,
        setIsAttachmentRequired,
        settingCepInternallyRef,
        activityRange,
        activityRangeLabel,
        formSubmit,
        clearValues,
        navigateBackOrHome,
        maskDecimal,
        hasRefuel,
        setHasRefuel
    };
}
